import { useQueryClient, type QueryState } from "@tanstack/react-query";
import { useLayoutEffect, useRef } from "react";
import { navigate } from "wouter/use-browser-location";
import { useTenantInstance } from "@/contexts/TenantInstanceContext";
import { ME_QUERY_KEY } from "@/hooks/use-me";
import type { MeResponse } from "@shared/types/auth";
import { setInstanceMismatchHandler } from "@/lib/api";
import { useNotice } from "@/lib/feedback/NoticeProvider";
import { forgetInstanceChoice } from "@/lib/instanceChoice";
import { splitInstancePath } from "@/lib/instancePath";

/** The notice's key: a burst of refused requests posts it once. */
export const INSTANCE_ACCESS_LOST_NOTICE = "instance-access-lost";

const PICKER_PATH = "/instances";

/**
 * Whether `/me`, read again successfully since `asked`, still lists the selected instance: then
 * the refusal was not lost access (review r9 #5) and nothing is re-picked.
 */
function meStillLists(
  state: QueryState<MeResponse> | undefined,
  selectedId: string | null,
  asked: number,
): boolean {
  return (
    selectedId !== null &&
    state?.status === "success" &&
    state.dataUpdatedAt >= asked &&
    (state.data?.instances.some((instance) => instance.id === selectedId) ?? false)
  );
}

/**
 * B-13 (round 9): a 401 `INSTANCE_MISMATCH` means the person's access to the instance on screen
 * ended mid-session (a grant revoked, the instance or its tenant disabled). The session itself is
 * fine, so this is NOT a sign-out: the instance choice is dropped, `/me` is read again for the
 * instances the person still reaches, and the server's sentence is posted. Inside the instance's
 * address the shell then shows its in-shell "no access" page (`InstanceUnavailable`); anywhere
 * else the picker opens. The person keeps their session; only the screen of the lost instance
 * goes.
 *
 * The server also answers `INSTANCE_MISMATCH` for a target that belongs to another instance than
 * the selected one (the IDOR guard, a binding id). That is not lost access, so `/me` decides
 * (review r9 #5): when it is read again successfully and still lists the selected instance,
 * nothing is re-picked and the page shows its own error. A `/me` that fails or no longer lists
 * it re-picks as above.
 *
 * Registered in a layout effect, like the session-expired handler, so a request fired from a
 * child's passive effect already has it.
 */
export function InstanceMismatchRecovery() {
  const { clearInstance, selectedInstance } = useTenantInstance();
  // Read when a request is refused, not a dependency: the context may clear the selection itself
  // once `/me` drops the instance, and the recovery must still post its sentence.
  const selectedRef = useRef<string | null>(null);
  const selectedId = selectedInstance?.id ?? null;
  useLayoutEffect(() => {
    selectedRef.current = selectedId;
  }, [selectedId]);
  const notice = useNotice();
  const queryClient = useQueryClient();

  useLayoutEffect(() => {
    setInstanceMismatchHandler((message) => {
      const selectedId = selectedRef.current;
      void (async () => {
        const asked = Date.now();
        await queryClient.invalidateQueries({ queryKey: ME_QUERY_KEY });
        const state = queryClient.getQueryState<MeResponse>(ME_QUERY_KEY);
        if (meStillLists(state, selectedId, asked)) return;
        forgetInstanceChoice();
        clearInstance();
        // Inside the instance's own address (round 9 T-58) the shell shows the in-shell "no
        // access" page once `/me` no longer lists it, with the switcher: no move to the picker.
        if (splitInstancePath(window.location.pathname).instanceId !== null) {
          notice.warning(message, { key: INSTANCE_ACCESS_LOST_NOTICE });
          return;
        }
        notice.warning(message, { key: INSTANCE_ACCESS_LOST_NOTICE, forPath: PICKER_PATH });
        if (window.location.pathname !== PICKER_PATH) navigate(PICKER_PATH);
      })();
    });
    return () => setInstanceMismatchHandler(null);
  }, [clearInstance, notice, queryClient]);

  return null;
}
